import uniq from "lodash-es/uniq";
import { stringify } from "query-string";
import { Reducer } from "redux";

import { serializeToQS } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollerSearchForm";
import {
    STROLLERS_SEARCH_LOAD_FACETS_API_SUCCESS,
    STROLLERS_SEARCH_LOAD_HELP_API_SUCCESS,
    STROLLERS_SEARCH_ON_OPEN_SELECT_FACET_VALUE,
    STROLLERS_SEARCH_RESULTS_FETCH_FAILURE,
    STROLLERS_SEARCH_RESULTS_FETCH_REQUEST,
    STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS,
    STROLLERS_SEARCH_TOGGLE_MORE_FACETS,
    SearchFacetsCounts,
    StrollersSearchLoadFacetsApiSuccessAction,
    StrollersSearchLoadHelpApiSuccessAction,
    StrollersSearchOnOpenSelectFacetValueAction,
    StrollersSearchResultsFetchFailureAction,
    StrollersSearchResultsFetchRequestAction,
    StrollersSearchResultsFetchSuccessAction,
    StrollersSearchToggleMoreFacetsAction,
} from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.actions";
import { FacetSetup } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.constants";
import { tupdate } from "@/mk2/helpers/types";
import { errorMessage } from "@/mk2/reducers/errorMessage";
import { LoadingState, loadingState } from "@/mk2/reducers/loadingState";

export interface SearchFacetsCountsAndHits {
    counts: SearchFacetsCounts;
    hits: number;
}

export interface StrollersSearchState {
    showAdditionalFacets: boolean;
    actuallyDisplayedFacet: FacetSetup;

    buyingGuideId: number;
    brandIds: number[];
    brandsCount: number;
    strollersCountPerBrand: Record<number, number>;
    reviewsCountPerBrand: Record<number, number>;

    searchFacetsCounts: Record<string, SearchFacetsCountsAndHits>;
    helpTexts: number[];

    results: StrollersResultsState;
}

export const initialStrollersSearchState: StrollersSearchState = {
    showAdditionalFacets: false,
    actuallyDisplayedFacet: null,

    buyingGuideId: null,
    brandIds: [],
    brandsCount: 0,
    strollersCountPerBrand: {},
    reviewsCountPerBrand: {},

    searchFacetsCounts: {},
    helpTexts: [],

    results: {},
};

export interface StrollersResultState {
    strollerIds: number[];
    strollersPreviewPhotoMap: Record<number, number>;
    variantIds: number[];
    variantsPreviewPhotoMap: Record<number, number>;
    strollersPhotosIds: number[];
    brandsOfQueryIds: number[];
    searchInfoIds: number[];
    loadingState: LoadingState;
    errorMessage: string;
    hasMore: boolean;
    count: number;
}

export const initialStrollersResultState: StrollersResultState = {
    strollerIds: [],
    strollersPreviewPhotoMap: {},
    variantIds: [],
    variantsPreviewPhotoMap: {},
    strollersPhotosIds: [],
    brandsOfQueryIds: [],
    searchInfoIds: [],
    loadingState: LoadingState.INIT,
    errorMessage: null,
    hasMore: false,
    count: null,
};

export interface StrollersResultsState {
    [q: string]: StrollersResultState;
}

declare type StrollersSearchReducerAction =
    | StrollersSearchToggleMoreFacetsAction
    | StrollersSearchOnOpenSelectFacetValueAction
    | StrollersSearchResultsFetchRequestAction
    | StrollersSearchResultsFetchSuccessAction
    | StrollersSearchResultsFetchFailureAction
    | StrollersSearchLoadFacetsApiSuccessAction
    | StrollersSearchLoadHelpApiSuccessAction;

export const strollersSearchReducer: Reducer<StrollersSearchState, StrollersSearchReducerAction> = (
    state = initialStrollersSearchState,
    action,
) => {
    switch (action.type) {
        case STROLLERS_SEARCH_TOGGLE_MORE_FACETS:
            return tupdate(state, {
                showAdditionalFacets: !state.showAdditionalFacets,
            });
        case STROLLERS_SEARCH_ON_OPEN_SELECT_FACET_VALUE:
            return tupdate(state, {
                actuallyDisplayedFacet: action.facetSetup,
            });

        case STROLLERS_SEARCH_RESULTS_FETCH_REQUEST:
        case STROLLERS_SEARCH_RESULTS_FETCH_FAILURE: {
            const q = action.q;
            const resultState = state.results[q] || initialStrollersResultState;
            return tupdate(state, {
                results: tupdate(state.results, {
                    [q]: tupdate(resultState, {
                        loadingState: loadingState(undefined, action),
                        errorMessage: errorMessage(undefined, action),
                    }),
                }),
            });
        }
        case STROLLERS_SEARCH_RESULTS_FETCH_SUCCESS: {
            const firstFetch = !action.offset;
            const q = action.q;
            const resultState = state.results[q] || initialStrollersResultState;
            return tupdate(state, {
                results: tupdate(state.results, {
                    [q]: tupdate(resultState, {
                        loadingState: loadingState(undefined, action),
                        errorMessage: errorMessage(undefined, action),
                        searchInfoIds: action.response.result.searchInfos,
                        strollerIds: uniq([
                            ...resultState.strollerIds,
                            ...action.response.result.strollers,
                        ]),
                        strollersPreviewPhotoMap: tupdate(
                            resultState.strollersPreviewPhotoMap,
                            action.response.result.strollersPreviewPhotoMap,
                        ),
                        variantIds: uniq([
                            ...resultState.variantIds,
                            ...action.response.result.variants,
                        ]),
                        variantsPreviewPhotoMap: tupdate(
                            resultState.variantsPreviewPhotoMap,
                            action.response.result.variantsPreviewPhotoMap,
                        ),
                        strollersPhotosIds: uniq([
                            ...resultState.strollersPhotosIds,
                            ...action.response.result.strollersPhotos,
                        ]),
                        brandsOfQueryIds: uniq([
                            ...resultState.brandsOfQueryIds,
                            ...action.response.result.brandsOfQuery,
                        ]),
                        hasMore: action.hasMore,
                        count: firstFetch ? action.count : resultState.count,
                    }),
                }),
                brandIds: firstFetch ? action.response.result.brands : state.brandIds,
                brandsCount: firstFetch ? action.brandsCount : state.brandsCount,
                strollersCountPerBrand: firstFetch
                    ? action.strollersCountPerBrand
                    : state.strollersCountPerBrand,
                reviewsCountPerBrand: firstFetch
                    ? action.reviewsCountPerBrand
                    : state.reviewsCountPerBrand,
                buyingGuideId: firstFetch
                    ? action.response.result.buyingGuide
                    : state.buyingGuideId,
            });
        }

        case STROLLERS_SEARCH_LOAD_FACETS_API_SUCCESS: {
            const key = stringify(serializeToQS(action.values));
            return {
                ...state,
                searchFacetsCounts: {
                    ...state.searchFacetsCounts,
                    [key]: { counts: action.searchFacetsCounts, hits: action.hits },
                },
            };
        }
        case STROLLERS_SEARCH_LOAD_HELP_API_SUCCESS:
            return {
                ...state,
                helpTexts: action.response.result.items,
            };

        default:
            return state;
    }
};

export default strollersSearchReducer;
